import { BLANK_ATYPICAL_SURCHARGE_ITEM, BLANK_PRODUCT_ATYPICAL_SURCHARGE_ITEM } from "../constants"
import type { CostBreakdownFormValues, CostBreakdownProductsFormValues } from "../schemas"

import { convertMetricPriceToUs, convertUsPriceToMetric } from "./productCalculations"

import { type GoodsReceipt, type OrderLine, ShippingType } from "@/graphql/codegen/graphql"
import { getGoodsReceiptPurchaseOrderLines } from "@/screens/WarehouseReceiving/utils"
import { isMetricUom, isUsUom } from "@/utils/conversion"

export const getCostBreakdownBaseFormValues = (goodsReceipt?: GoodsReceipt): CostBreakdownFormValues => {
  const costBreakdown = goodsReceipt?.costBreakdown

  if (!costBreakdown) {
    return {
      shippingCosts: [
        { grShippingCostId: null, shippingType: ShippingType.Ocean, cost: null },
        { grShippingCostId: null, shippingType: ShippingType.Inland, cost: null },
      ],
      importFees: null,
      atypicalSurcharges: [BLANK_ATYPICAL_SURCHARGE_ITEM],
      sharedGrCost: null,
    }
  }

  const shippingCosts = costBreakdown.shippingCosts?.length
    ? costBreakdown.shippingCosts.map((shippingCost) => ({
        grShippingCostId: shippingCost.grShippingCostId,
        shippingType: shippingCost.shippingType,
        cost: shippingCost.cost ?? null,
      }))
    : [
        { grShippingCostId: null, shippingType: ShippingType.Ocean, cost: null },
        { grShippingCostId: null, shippingType: ShippingType.Inland, cost: null },
      ]

  const atypicalSurcharges = costBreakdown.atypicalSurcharges?.length
    ? costBreakdown.atypicalSurcharges.map((surcharge) => ({
        grAtypicalSurchargeId: surcharge.grAtypicalSurchargeId,
        description: surcharge.description,
        amount: surcharge.amount ?? null,
      }))
    : [BLANK_ATYPICAL_SURCHARGE_ITEM]

  const importFees = costBreakdown.importFees ?? null
  const sharedGrCost = costBreakdown.sharedGrCost ?? null

  return {
    shippingCosts,
    importFees,
    atypicalSurcharges,
    sharedGrCost,
  }
}

/**
 * Gets the final form default values by merging base defaults with form values
 * Handles the logic of when to use form values vs fallback to defaults for arrays
 */
export const getCostBreakdownDefaultFormValues = (
  goodsReceipt?: GoodsReceipt,
  formValues?: CostBreakdownFormValues
): CostBreakdownFormValues => {
  const baseDefaults = getCostBreakdownBaseFormValues(goodsReceipt)

  if (!formValues) {
    return baseDefaults
  }

  return {
    ...baseDefaults,
    ...formValues,
    // Use form values for arrays only if they have content, otherwise use defaults
    shippingCosts: formValues.shippingCosts?.length ? formValues.shippingCosts : baseDefaults.shippingCosts,
    atypicalSurcharges: formValues.atypicalSurcharges?.length
      ? formValues.atypicalSurcharges
      : baseDefaults.atypicalSurcharges,
  }
}

const getPricePerUom = (productOrderLine: OrderLine) => {
  const { priceUomId, pricePerUom } = productOrderLine

  if (!(pricePerUom && pricePerUom > 0)) {
    return null
  }

  if (isUsUom(priceUomId)) {
    return pricePerUom
  }

  if (isMetricUom(priceUomId)) {
    return convertMetricPriceToUs(pricePerUom, productOrderLine.product, priceUomId)
  }

  return null
}

const getPricePerMetricUom = (productOrderLine: OrderLine) => {
  const { priceUomId, pricePerUom } = productOrderLine

  if (!(pricePerUom && pricePerUom > 0)) {
    return null
  }

  if (isMetricUom(priceUomId)) {
    return pricePerUom
  }

  if (isUsUom(priceUomId)) {
    return convertUsPriceToMetric(pricePerUom, productOrderLine.product, priceUomId)
  }

  return null
}

export const generateCostBreakdownProductsDefaults = (
  goodsReceipt?: GoodsReceipt,
  grCostBreakdown?: CostBreakdownFormValues | undefined
): CostBreakdownProductsFormValues => {
  const purchaseOrderLines = goodsReceipt ? getGoodsReceiptPurchaseOrderLines(goodsReceipt) : []

  return {
    products: purchaseOrderLines.map((line) => ({
      pricePerUom: getPricePerUom(line),
      pricePerMetricUom: getPricePerMetricUom(line),
      bareCostPerUom: null,
      bareCostPerMetricUom: null,
      totalBareCost: null,
      insurance: null,
      tariffs: [
        {
          amount: null,
          percentage: null,
        },
      ],
      duties: {
        amount: null,
        percentage: null,
      },
      mpf: {
        amount: null,
        percentage: null,
      },
      hmf: {
        amount: null,
        percentage: null,
      },
      atypicalSurcharges: [BLANK_PRODUCT_ATYPICAL_SURCHARGE_ITEM],
      allocatedGrCostPercentage: null,
      allocatedGrCostAmount: null,
      allocatedGrCostPerUom: null,
      lastLaidDownCostPerUom: null,
      totalLaidDownCostPerUom: null,
      isCompleted: false,
    })),
    sharedGrCost: grCostBreakdown?.sharedGrCost ?? null,
  }
}

/**
 * Merges preserved user data with fresh defaults, keeping user-entered fields
 * but resetting only sharedGrCost-dependent fields when sharedGrCost changes
 */
export const getCostBreakdownProductsDefaultValues = (
  goodsReceipt: GoodsReceipt | undefined,
  grCostBreakdown: CostBreakdownFormValues | undefined,
  preservedData: CostBreakdownProductsFormValues | undefined
): CostBreakdownProductsFormValues => {
  // If no preserved data, generate fresh defaults
  if (!preservedData) {
    return generateCostBreakdownProductsDefaults(goodsReceipt, grCostBreakdown)
  }

  // Generate fresh defaults to get the correct structure and new sharedGrCost
  const freshDefaults = generateCostBreakdownProductsDefaults(goodsReceipt, grCostBreakdown)

  return {
    // Always use the new sharedGrCost
    sharedGrCost: grCostBreakdown?.sharedGrCost ?? null,

    // Merge product data - preserve user data but reset sharedGrCost-dependent fields
    products: freshDefaults.products.map((freshProduct, index) => {
      const preservedProduct = preservedData.products?.[index]

      if (!preservedProduct) {
        return freshProduct
      }

      return {
        // Keep user-entered fields
        pricePerUom: preservedProduct.pricePerUom || freshProduct.pricePerUom,
        pricePerMetricUom: preservedProduct.pricePerMetricUom || freshProduct.pricePerMetricUom,
        bareCostPerUom: preservedProduct.bareCostPerUom || null,
        bareCostPerMetricUom: preservedProduct.bareCostPerMetricUom || null,
        totalBareCost: preservedProduct.totalBareCost || null,
        insurance: preservedProduct.insurance || null,
        duties:
          preservedProduct.duties?.amount !== null && preservedProduct.duties?.amount !== undefined
            ? preservedProduct.duties
            : freshProduct.duties,
        mpf:
          preservedProduct.mpf?.amount !== null && preservedProduct.mpf?.amount !== undefined
            ? preservedProduct.mpf
            : freshProduct.mpf,
        hmf:
          preservedProduct.hmf?.amount !== null && preservedProduct.hmf?.amount !== undefined
            ? preservedProduct.hmf
            : freshProduct.hmf,
        tariffs: preservedProduct.tariffs?.length ? preservedProduct.tariffs : freshProduct.tariffs,
        atypicalSurcharges: preservedProduct.atypicalSurcharges?.length
          ? preservedProduct.atypicalSurcharges
          : freshProduct.atypicalSurcharges,
        lastLaidDownCostPerUom: preservedProduct.lastLaidDownCostPerUom || null,
        totalLaidDownCostPerUom: preservedProduct.totalLaidDownCostPerUom || null,
        isCompleted: preservedProduct.isCompleted || false,

        // Reset sharedGrCost-dependent fields - these will be recalculated
        allocatedGrCostPercentage: null,
        allocatedGrCostAmount: null,
        allocatedGrCostPerUom: null,
      }
    }),
  }
}
